import React from "react";
import { Box, Grid, HStack, Icon, Select, Stack, Text, createListCollection, useSelectContext } from "@chakra-ui/react";
import { DashboardDeleteButton, DashboardSelectTrigger, DashboardSelectContent, DashboardSelectItem, DashboardTruncatedText } from "components/utils/FormControls";
import { Button } from "components/ui/Button";
import { MdAssignment, MdAttachMoney, MdHowToReg, MdLocalCafe, MdRecordVoiceOver, MdSports } from "react-icons/md";
import { FiPlus } from "react-icons/fi";
import { AvatarSelectField } from "components/utils/AvatarSelectField";
import AppAvatar from "components/utils/AppAvatar";
import { DataCard } from "components/utils/DataCard";
import { FormDialog } from "components/utils/FormDialog";
import { useDialogForm } from "utils/useDialogForm";
import { useDashboardActividadRosters, useDashboardActividadEncargos, useCreateDashboardActividadEncargo, usePatchDashboardActividadEncargo, useDeleteDashboardActividadEncargo } from "features/dashboard/actividades/hooks";
import { getAvatarHexColor, getAvatarTint, resolveAvatarUrl } from "utils/avatar";
import { isPrefixedTempId, makePrefixedTempId, normalizeRosterRows } from "../shared/helpers";

const DEFAULT_ROLE_OPTIONS = [
  { id: "actividad", label: "Actividad", icon: MdSports, iconColor: "arawaks.skyBlue" },
  { id: "charla", label: "Charla", icon: MdRecordVoiceOver, iconColor: "arawaks.grapePurple" },
  { id: "refrigerio", label: "Refrigerio", icon: MdLocalCafe, iconColor: "arawaks.adventureOrange" },
  { id: "cobranza", label: "Cobranza", icon: MdAttachMoney, iconColor: "arawaks.jungleGreen" },
  { id: "asistencia", label: "Asistencia", icon: MdHowToReg, iconColor: "arawaks.campfireRed" },
];
const REQUIRED_ROLE_IDS = ["actividad", "charla"];

const TMP_PREFIX = "enc_";

function rowTone(seed) {
  const solid = getAvatarHexColor(seed);
  return { solid, bg: getAvatarTint(seed, 12) };
}

function SelectValueRole({ placeholder }) {
  const select = useSelectContext();
  const item = (select.selectedItems || [])[0];
  const IconCmp = item?.icon;
  return (
    <Select.ValueText placeholder={placeholder}>
      {item ? (
        <HStack gap="2" w="full" justify="flex-start">
          <Icon color={item?.iconColor || "dashboard.fg.muted"}>{IconCmp ? <IconCmp /> : null}</Icon>
          <Text flex="1" textAlign="left">{item.label}</Text>
        </HStack>
      ) : (
        <Text color="dashboard.fg.muted" textAlign="left">{placeholder}</Text>
      )}
    </Select.ValueText>
  );
}

function RoleSelect({ collection, value, onChange }) {
  return (
    <Select.Root
      collection={collection}
      size="sm"
      value={value ? [value] : []}
      onValueChange={(e) => onChange(e.value?.[0] ?? "")}
      positioning={{ strategy: "fixed", hideWhenDetached: true }}
    >
      <Select.HiddenSelect />
      <Select.Control>
        <DashboardSelectTrigger textAlign="left">
          <SelectValueRole placeholder="Selecciona encargo" />
        </DashboardSelectTrigger>
        <Select.IndicatorGroup>
          <Select.Indicator color="dashboard.accent.solid" />
        </Select.IndicatorGroup>
      </Select.Control>
      <Select.Positioner>
        <DashboardSelectContent>
          {collection.items.map((item) => {
            const IconCmp = item.icon;
            return (
              <DashboardSelectItem key={item.id} item={item}>
                <Icon color={item.iconColor || "dashboard.fg.muted"}>{IconCmp ? <IconCmp /> : null}</Icon>
                <Text flex="1" textAlign="left">{item.label}</Text>
                <Select.ItemIndicator />
              </DashboardSelectItem>
            );
          })}
        </DashboardSelectContent>
      </Select.Positioner>
    </Select.Root>
  );
}

export const PrevioEncargosCard = ({ actividadId, users }) => {
  const dialog = useDialogForm({ encargos: [] });
  const [localError, setLocalError] = React.useState(null);
  const originalRef = React.useRef([]);

  const rosterQ = useDashboardActividadRosters(true);
  const encargosQ = useDashboardActividadEncargos(actividadId, !!actividadId);

  const createEncargo = useCreateDashboardActividadEncargo();
  const patchEncargo = usePatchDashboardActividadEncargo();
  const deleteEncargo = useDeleteDashboardActividadEncargo();

  const saving = !!(createEncargo.isPending || patchEncargo.isPending || deleteEncargo.isPending);
  const apiError = createEncargo.isError ? String(createEncargo.error?.message || createEncargo.error) : patchEncargo.isError ? String(patchEncargo.error?.message || patchEncargo.error) : deleteEncargo.isError ? String(deleteEncargo.error?.message || deleteEncargo.error) : null;
  const errorText = localError || apiError;

  const rowsFromDb = React.useMemo(() => {
    const list = encargosQ.data ?? [];
    return (Array.isArray(list) ? list : []).map((e) => ({
      id: e.id,
      userId: e.instructorId,
      roleId: e.encargo,
      instructorNombre: e.instructorNombre || "Instructor",
      avatar: resolveAvatarUrl(e),
    }));
  }, [encargosQ.data]);

  const members = React.useMemo(() => {
    const roster = normalizeRosterRows(rosterQ.data?.instructoresPeriodo, {
      idKeys: ["id", "instructorId"],
      nameKeys: ["nombre", "instructorNombre"],
      fallbackName: "Instructor",
    });
    const membersMap = new Map();

    if (roster.length) {
      for (const r of roster) {
        const id = r.id;
        const name = r.nombre || "Instructor";
        if (!id) continue;
        membersMap.set(id, {
          id,
          name,
          avatar: resolveAvatarUrl(r),
          role: "Instructor",
        });
      }
    } else {
      for (const u of users ?? []) {
        const id = u.id;
        const name = u.name || u.nombre || "Instructor";
        if (!id) continue;
        membersMap.set(id, {
          id,
          name,
          avatar: resolveAvatarUrl(u),
          role: u.role || "Instructor",
        });
      }
    }

    for (const row of rowsFromDb) {
      if (!row.userId) continue;
      const existing = membersMap.get(row.userId);
      if (existing) {
        if (!existing.avatar && row.avatar) {
          existing.avatar = row.avatar;
        }
      } else {
        const name = row.instructorNombre || "Instructor";
        membersMap.set(row.userId, {
          id: row.userId,
          name,
          avatar: row.avatar,
          role: "Instructor",
        });
      }
    }

    return Array.from(membersMap.values());
  }, [rosterQ.data, rowsFromDb, users]);

  const memberById = React.useMemo(() => new Map(members.map((m) => [m.id, m])), [members]);
  const roleById = React.useMemo(() => new Map(DEFAULT_ROLE_OPTIONS.map((r) => [r.id, r])), []);
  const membersCollection = React.useMemo(
    () => createListCollection({ items: members, itemToString: (x) => x.name, itemToValue: (x) => x.id }),
    [members]
  );
  const rolesCollection = React.useMemo(
    () => createListCollection({ items: DEFAULT_ROLE_OPTIONS, itemToString: (x) => x.label, itemToValue: (x) => x.id }),
    []
  );

  function openDialog() {
    setLocalError(null);
    originalRef.current = rowsFromDb;
    dialog.openWith({ encargos: rowsFromDb });
  }

  function addRow() {
    dialog.setField("encargos", [...(dialog.form?.encargos || []), { id: makePrefixedTempId(TMP_PREFIX), userId: "", roleId: "" }]);
  }

  function removeRow(rowId) {
    dialog.setField(
      "encargos",
      (dialog.form?.encargos || []).filter((r) => r.id !== rowId)
    );
  }

  function patchRow(rowId, patchRowData) {
    dialog.setField(
      "encargos",
      (dialog.form?.encargos || []).map((r) => (r.id === rowId ? { ...r, ...patchRowData } : r))
    );
  }

  async function save() {
    setLocalError(null);

    const next = Array.isArray(dialog.form?.encargos) ? dialog.form.encargos : [];

    // enforce required fields (ignore empty rows is NOT allowed)
    const hasIncomplete = next.some((r) => !r?.userId || !r?.roleId);
    if (hasIncomplete) return setLocalError("Completa instructor y encargo en todas las filas (o elimina las filas vacías).");

    const assignedRoles = new Set(next.map((r) => String(r?.roleId || "").toLowerCase()));
    const missingRoles = REQUIRED_ROLE_IDS.filter((roleId) => !assignedRoles.has(roleId));
    if (missingRoles.length > 0) {
      const missingLabels = missingRoles.map((roleId) => roleById.get(roleId)?.label || roleId).join(" y ");
      return setLocalError(`Asigna al menos un instructor al encargo: ${missingLabels}.`);
    }

    const prev = Array.isArray(originalRef.current) ? originalRef.current : [];
    const prevById = new Map(prev.filter((r) => r?.id && !isPrefixedTempId(r.id, TMP_PREFIX)).map((r) => [r.id, r]));
    const nextById = new Map(next.filter((r) => r?.id && !isPrefixedTempId(r.id, TMP_PREFIX)).map((r) => [r.id, r]));

    const toDelete = prev.filter((r) => r?.id && !isPrefixedTempId(r.id, TMP_PREFIX) && !nextById.has(r.id));
    const toCreate = next.filter((r) => !r?.id || isPrefixedTempId(r.id, TMP_PREFIX) || !prevById.has(r.id));
    const toPatch = next.filter((r) => r?.id && !isPrefixedTempId(r.id, TMP_PREFIX) && prevById.has(r.id)).filter((r) => {
      const p = prevById.get(r.id);
      return (p.userId !== r.userId) || (p.roleId !== r.roleId);
    });

    try {
      // delete first to avoid unique constraints (if any)
      for (const r of toDelete) await deleteEncargo.mutateAsync({ actividadId, encargoId: r.id });
      for (const r of toCreate) await createEncargo.mutateAsync({ actividadId, payload: { instructorId: r.userId, encargo: r.roleId } });
      for (const r of toPatch) await patchEncargo.mutateAsync({ actividadId, encargoId: r.id, patch: { instructorId: r.userId, encargo: r.roleId } });

      dialog.close();
    } catch (e) {
      setLocalError(String(e?.message || e));
      throw e;
    }
  }

  const displayRows = React.useMemo(() => {
    // prefer DB rows; if none, show nothing
    return rowsFromDb.map((r) => {
      const m = memberById.get(r.userId);
      const name = m?.name || r.instructorNombre || "Instructor";
      const avatar = m?.avatar || r.avatar;
      const role = roleById.get(r.roleId);
      const label = role?.label || r.roleId || "Encargo";
      const RoleIcon = role?.icon;
      const seed = r.userId || name;
      return { key: r.id, seed, name, avatar, label, RoleIcon };
    });
  }, [rowsFromDb, memberById, roleById]);

  const missingRequiredRoles = React.useMemo(() => {
    const assignedRoles = new Set(rowsFromDb.map((row) => String(row.roleId || "").toLowerCase()));
    return REQUIRED_ROLE_IDS.filter((roleId) => !assignedRoles.has(roleId));
  }, [rowsFromDb]);

  return (
    <>
      <DataCard title="Encargos" variant="cyan" headerIcon={<MdAssignment size="24px" />} onEdit={openDialog} editAriaLabel="Editar encargos" isIncomplete={!encargosQ.isLoading && missingRequiredRoles.length > 0}>
        {encargosQ.isLoading ? (
          <Text color="dashboard.fg.muted" fontSize="sm">Cargando encargos…</Text>
        ) : displayRows.length === 0 ? (
          <Stack gap="1">
            <Text color="dashboard.fg.muted" fontSize="sm">Sin encargos asignados.</Text>
          </Stack>
        ) : (
          <Grid templateColumns={{ base: "minmax(0, 1fr)", md: "repeat(2, minmax(0, 1fr))" }} gap="2.5" minW="0">
            {displayRows.map((row) => {
              const tone = rowTone(row.seed);
              return (
                <Box key={row.key} rounded="2xl" p="3" bg={tone.bg} borderWidth="1px" borderColor="dashboard.border.muted" minW="0" overflow="hidden">
                  <HStack gap="2.5" minW="0">
                    <AppAvatar name={row.name} src={row.avatar} seed={row.seed} />
                    <Stack gap="1" minW="0" flex="1">
                      <DashboardTruncatedText fontWeight="800" fontSize="sm" color="dashboard.fg.default">
                        {row.name}
                      </DashboardTruncatedText>
                      <Box alignSelf="start" rounded="pill" px="2.5" py="0.5" bg={tone.solid} color="white" fontSize="xs" fontWeight="700" maxW="full">
                        <HStack gap="1.5" minW="0">
                          <Icon color="white" flexShrink={0}>{row.RoleIcon ? <row.RoleIcon /> : null}</Icon>
                          <Text as="span" fontSize="xs" fontWeight="800" color="white" truncate>
                            {row.label}
                          </Text>
                        </HStack>
                      </Box>
                    </Stack>
                  </HStack>
                </Box>
              );
            })}
          </Grid>
        )}
      </DataCard>

      <FormDialog open={dialog.open} onOpenChange={dialog.onOpenChange} title="Editar Encargos" errorText={errorText} saving={saving} onCancel={dialog.close} onSave={save}>
        <Box>
          <HStack justify="space-between" mb="2">
            <Text fontSize="sm" fontWeight="600" color="dashboard.fg.default">Encargos</Text>
            <Button variant="surface" color="red" size="sm" onClick={addRow}>
              <HStack gap="2">
                <FiPlus />
                <Text>Agregar</Text>
              </HStack>
            </Button>
          </HStack>

          <Grid templateColumns="2fr 2fr auto" gap="3" px="2" py="2" bg="dashboard.bg.surfaceMuted" rounded="md">
            <Text fontSize="sm" fontWeight="600" color="dashboard.fg.muted">Instructor</Text>
            <Text fontSize="sm" fontWeight="600" color="dashboard.fg.muted">Encargo</Text>
            <Text fontSize="sm" fontWeight="600" color="dashboard.fg.muted"></Text>
          </Grid>

          <Stack gap="2" mt="2">
            {(dialog.form?.encargos || []).map((row) => (
              <Grid key={row.id} templateColumns="2fr 2fr auto" gap="3" px="2" py="2" alignItems="center">
                <AvatarSelectField
                  collection={membersCollection}
                  value={row.userId}
                  onChange={(v) => patchRow(row.id, { userId: v })}
                  placeholder="Selecciona instructor"
                />
                <RoleSelect
                  collection={rolesCollection}
                  value={row.roleId}
                  onChange={(v) => patchRow(row.id, { roleId: v })}
                />
                <DashboardDeleteButton aria-label="Eliminar encargo" onClick={() => removeRow(row.id)} />
              </Grid>
            ))}
          </Stack>
        </Box>
      </FormDialog>
    </>
  );
};
